import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore, useEnabledStore } from "@/stores";

const HomeView = () => import("@/views/HomeView.vue");
const HomeViewAuth = () => import("@/views/HomeViewAuth.vue");

const LoginPage = () => import("@/views/auth/Login.vue");
const ForgotPassword = () => import("@/views/auth/ForgotPassword.vue");
const ResetPassword = () => import("@/views/auth/ResetRassword.vue");
const ResetPasswordFailed = () =>
  import("@/views/auth/ResetPasswordFailed.vue");
const SignUp = () => import("@/views/payment/SignUp.vue");
const Checkout = () => import("@/views/payment/Checkout.vue");
const PaymentApproved = () => import("@/views/payment/PaymentApproved.vue");
const PaymentDenied = () => import("@/views/payment/PaymentDenied.vue");
const TermsOfUse = () => import("@/views/terms/TermsOfUse.vue");
const PrivacyPolicy = () => import("@/views/terms/PrivacyPolicy.vue");
const BillingTerms = () => import("@/views/terms/BillingTerms.vue");
const ContactUs = () => import("@/views/ContactUs.vue");
const PersonalInfo = () => import("@/views/my-profile/PersonalInfo.vue");
const NotFound = () => import("@/views/NotFound.vue");

// import routes for the modules
import books from "./books";
import beauty from "./beauty";

const subdomain = window.location.hostname.split(".")[0];

const routes = [
  {
    path: "/",
    name: "home",
    component: HomeView,
    meta: { hideForAuth: true },
  },
  {
    path: "/home",
    name: "home-auth",
    component: HomeViewAuth,
    meta: { requiresAuth: true, allowedSubdomains: ["portal"] },
  },
  {
    path: "/login",
    name: "login",
    component: LoginPage,
    meta: { hideForAuth: true, allowedSubdomains: ["portal"] },
  },
  {
    path: "/forgot-password",
    name: "forgot-password",
    component: ForgotPassword,
    meta: { allowedSubdomains: ["portal"] },
  },
  {
    path: "/change-password/:resetKey",
    name: "change-password",
    component: ResetPassword,
  },
  {
    path: "/reset-password-failed",
    name: "reset-password-failed",
    component: ResetPasswordFailed,
  },
  {
    path: "/sign-up",
    name: "sign-up",
    component: SignUp,
    meta: { hideForAuth: true, allowedSubdomains: ["payment"] },
  },
  {
    path: "/checkout",
    name: "checkout",
    component: Checkout,
    meta: { hideForAuth: true, allowedSubdomains: ["payment"] },
  },
  {
    path: "/approved",
    name: "approved",
    component: PaymentApproved,
    meta: { allowedSubdomains: ["payment"] },
  },
  {
    path: "/denied",
    name: "denied",
    component: PaymentDenied,
    meta: { allowedSubdomains: ["payment"] },
  },
  {
    path: "/terms",
    name: "terms",
    component: TermsOfUse,
    meta: { allowedSubdomains: ["portal"] },
  },
  {
    path: "/privacy",
    name: "privacy",
    component: PrivacyPolicy,
    meta: { allowedSubdomains: ["portal"] },
  },
  {
    path: "/cancellation",
    name: "cancellation",
    component: BillingTerms,
    meta: { allowedSubdomains: ["portal"] },
  },
  {
    path: "/contact-us",
    name: "contact-us",
    component: ContactUs,
    meta: { allowedSubdomains: ["portal"] },
  },
  {
    path: "/my-profile/personal-info",
    name: "personal-info",
    component: PersonalInfo,
    meta: { requiresAuth: true, allowedSubdomains: ["portal"] },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: NotFound,
  },
  ...books,
  ...beauty,
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.query.search) {
      return;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

router.beforeEach(async (to) => {
  const store = useAuthStore();
  await store.checkAuthStatus();
  const userIsAuth = store.isAuth;

  if (
    to.meta.allowedSubdomains &&
    !to.meta.allowedSubdomains.includes(subdomain)
  ) {
    return { name: "not-found" };
  }

  if (to.meta.requiresAuth && !userIsAuth) {
    const { port, protocol } = window.location;
    window.location.replace(
      `${protocol}//portal.${import.meta.env.VITE_DOMAIN}${port ? `:${port}` : ""}/login`
    );
  }

  if (to.meta.hideForAuth && userIsAuth) {
    const { port, protocol } = window.location;
    window.location.replace(
      `${protocol}//portal.${import.meta.env.VITE_DOMAIN}${port ? `:${port}` : ""}/home`
    );
  }

  const enableStore = useEnabledStore();
  await enableStore.checkPortalType();
  const portalTypeIsBooks = enableStore.isPortalTypeBooks;
  if (to.meta.hideForPortalTypeBooks && portalTypeIsBooks) {
    const { port, protocol } = window.location;
    window.location.replace(
      `${protocol}//portal.${import.meta.env.VITE_DOMAIN}${port ? `:${port}` : ""}/`
    );
  }
});

export default router;
